import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { useConditionalFreeze } from '@/domain/hooks/useConditionalFreeze'
import { InjectedActionsContext, Objective } from '@/features/actions/logic/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { UseFormReturn, useForm, useWatch } from 'react-hook-form'
import { getFormFieldsForWithdrawDialog, getWithdrawDialogFormValidator } from './form'
import { PositionOverview } from './types'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'

import {
  AssetInputSchema,
  DialogFormNormalizedData,
  UseDebouncedDialogFormValuesResult,
} from '@/features/dialogs/common/logic/form'
import { useDebounce } from '@/utils/useDebounce'
import { MyWalletProps } from '@/features/isolated-details/components/my-wallet/MyWallet'

export interface UseDebouncedDialogFormValuesArgs {
  form: UseFormReturn<AssetInputSchema>
  walletOverview: MyWalletProps
}

export function useDebouncedDialogFormValues({
  form,
  walletOverview,
}: UseDebouncedDialogFormValuesArgs): UseDebouncedDialogFormValuesResult {
  const formValues = useWatch({ control: form.control })
  const isFormValid = form.formState.isValid

  const [keyCounter, setKeyCounter] = useState(0)
  const prevFormValuesRef = useRef(formValues)
  const prevIsFormValidRef = useRef(isFormValid)

  const normalizedFormValues = {
    token: walletOverview.withdrawBorrowAsset?.token,
    value: NormalizedUnitNumber(formValues?.value || '0'),
    isMaxSelected: formValues?.isMaxSelected || false,
    position: {
      collateralBalance: NormalizedUnitNumber(0),
    },
    reserve: {
      supplyAPY: Percentage(0),
    },
  } as DialogFormNormalizedData

  // Update key when form values change
  useEffect(() => {
    const prevValues = prevFormValuesRef.current
    const prevIsValid = prevIsFormValidRef.current

    // Check for changes in important properties
    const hasChanged =
      prevValues?.symbol !== formValues?.symbol ||
      prevValues?.value !== formValues?.value ||
      prevValues?.isMaxSelected !== formValues?.isMaxSelected ||
      prevIsValid !== isFormValid

    if (hasChanged) {
      prevFormValuesRef.current = formValues
      prevIsFormValidRef.current = isFormValid
      setKeyCounter((prev) => prev + 1)
    }
  }, [formValues, isFormValid])

  const key = `form-change-${keyCounter}`

  const { debouncedValue, isDebouncing } = useDebounce({ formValues: normalizedFormValues, isFormValid }, key)

  return {
    debouncedFormValues: debouncedValue.formValues,
    isFormValid: debouncedValue.isFormValid,
    isDebouncing,
  }
}

export interface UseWithdrawDialogResult {
  withdrawOptions: TokenWithBalance[]
  assetsToWithdrawFields: FormFieldsForDialog
  withdrawAsset: TokenWithValue
  objectives: Objective[]
  actionsContext: InjectedActionsContext
  pageStatus: PageStatus
  form: UseFormReturn<AssetInputSchema>
  currentPositionOverview: PositionOverview
  updatedPositionOverview?: PositionOverview
}

export function useLendWithdrawDialog({ walletOverview }: { walletOverview: MyWalletProps }): UseWithdrawDialogResult {
  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const isLimitedByPool = walletOverview.borrow.poolAvailable?.isLessThan(
    walletOverview.withdrawBorrowAsset?.available || NormalizedUnitNumber(0),
  )
  const effectiveAvailable =
    isLimitedByPool && walletOverview.borrow.poolAvailable
      ? walletOverview.borrow.poolAvailable
      : walletOverview.withdrawBorrowAsset?.available || NormalizedUnitNumber(0)

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(
      getWithdrawDialogFormValidator({
        deposited: walletOverview.withdrawBorrowAsset?.available || NormalizedUnitNumber(0),
        availableLiquidity: walletOverview.borrow.poolAvailable || NormalizedUnitNumber(0),
      }),
      undefined,
      { raw: true },
    ),
    defaultValues: {
      symbol: walletOverview.withdrawBorrowAsset?.token.symbol,
      value: '',
    },
    mode: 'onChange',
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedDialogFormValues({
    form,
    walletOverview,
  })

  const withdrawAsset = useConditionalFreeze(formValues, pageStatus === 'success')

  const assetsToWithdrawFields = getFormFieldsForWithdrawDialog(
    form,
    walletOverview.withdrawBorrowAsset!.token,
    walletOverview.withdrawBorrowAsset!.available,
    effectiveAvailable,
  )

  const updatedTokenSupply = NormalizedUnitNumber(0)

  const currentPositionOverview = {
    healthFactor: Percentage(0.8),
    tokenSupply: formValues.position.collateralBalance,
    supplyAPY: withdrawAsset.reserve.supplyAPY,
  }

  const updatedUserSummary = {
    healthFactor: Percentage(0.5),
    tokenSupply: undefined,
  }

  const updatedPositionOverview = withdrawAsset.value.eq(0)
    ? undefined
    : {
        ...currentPositionOverview,
        healthFactor: updatedUserSummary.healthFactor,
        tokenSupply: updatedTokenSupply,
      }

  const actionsEnabled = withdrawAsset.value.gt(0) && isFormValid && !isDebouncing

  return {
    form,
    withdrawOptions: [{ token: walletOverview.withdraw!.token, balance: walletOverview.withdraw!.available }],
    assetsToWithdrawFields,
    withdrawAsset,
    objectives: [
      {
        type: 'isolatedLendWithdraw',
        token: walletOverview.withdrawBorrowAsset!.token,
        value: withdrawAsset.value,
      },
    ],
    actionsContext: {
      isolatedInfo: {
        pairAddress: walletOverview.pairAddress,
      },
    },
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    currentPositionOverview,
    updatedPositionOverview,
  }
}
